import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { FiArrowRight, FiLogOut, FiSettings } from "react-icons/fi";
import { WithClassName, useIsScrolled } from "../../helper/util";
import { useUrl } from "../../hooks/common/useUrl";
import { useGetSeasons } from "../../hooks/season";
import { Season } from "../../models/Season";
import { AddPlayersView } from "../../pages/admin/[seasonId]/players";
import { AddTeamsView } from "../../pages/admin/[seasonId]/teams";
import { AddVenuesView } from "../../pages/admin/[seasonId]/venues";
import { LanguageSwitch } from "../LanguageSwitch";
import { Modal, ModalOpenButton } from "./Dialog";
import { QueryWrapper } from "./QueryWrapper";

function getCurrentlink(pathname: string) {
  return pathname.split("/")[3] ?? undefined;
}

function getTitle(category: string) {
  switch (category) {
    case "players":
      return "Nav_Players";
    case "venues":
      return "Nav_Venues";
    case "teams":
      return "Nav_Teams";
    default:
      return "Nav_Home";
  }
}

interface AddButtonProps {
  category: string;
  seasonId: string;
}
function AddButton({ category, seasonId }: AddButtonProps) {
  switch (category) {
    case "players":
      return <AddPlayersView seasonId={seasonId} />;
    case "venues":
      return <AddVenuesView />;
    case "teams":
      return <AddTeamsView />;
    default:
      return null;
  }
}

export function Nav() {
  const { seasonId } = useUrl();
  const { data: seasons, ...seasonsResult } = useGetSeasons();

  return (
    <QueryWrapper loaderType="big" useQueryResult={[seasonsResult]}>
      {seasons && <NavDisplay seasons={seasons} seasonId={seasonId} />}
    </QueryWrapper>
  );
}

interface NavDisplayProps {
  seasonId: string;
  seasons: Season[];
}
function NavDisplay({ seasonId, seasons }: NavDisplayProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { pathname, push } = useRouter();
  const { changeSeasonAndPush } = useUrl();
  // const { data: season, ...seasonResult } = useGetSeason({ id: seasonId });

  const season = seasons.find((s) => s.id === seasonId);
  async function logout() {
    const res = await fetch("/api/auth/logout");
    if (res.ok) {
      push("/adminlogin");
    }
  }

  const hasSubnav = ["pokal", "bundesliga", "settings"].includes(
    getCurrentlink(pathname)
  );

  const categoryIsLast =
    getCurrentlink(pathname) === pathname.split("/").slice(-1)[0];

  const { scrolled } = useIsScrolled();

  return (
    <>
      <div
        className={`bg-white header flex gap-4 pt-4 pb-4 px-4 bg-whit ${
          scrolled && "shadow-md"
        }`}
      >
        <div className="brand flex">
          <span className="text-4xl pr-3 self-center">TTBL ADMIN</span>
          <span className="hr-horizontal self-center"></span>
        </div>
        {season && (
          <ModalOpenButton setOpen={setModalOpen}>
            <button className="btnNavSeason self-center">
              {season.startYear} / {season.endYear}
              <FiArrowRight />
            </button>
          </ModalOpenButton>
        )}
        <Modal open={modalOpen} setOpen={setModalOpen}>
          <div className="container">
            <div className="bg-white rounded-b-lg px-8 md:px-8 py-8 mt-5 mb-10">
              <h2>Saison auswählen</h2>
              <div>
                Bitte wähle die Saison aus, die im Admin System bearbeitet
                werden soll.
              </div>
              <hr className="my-5" />
              <div className="flex flex-col">
                {seasons.map((s) => (
                  <button
                    key={s.id}
                    className="btnTertiary"
                    onClick={() => {
                      changeSeasonAndPush(s.id, "/");
                      setModalOpen(false);
                    }}
                  >
                    {s.startYear} / {s.endYear}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal>
        <div className="flex">
          <span className="hr-horizontal self-center"></span>
        </div>
        <div className="beNav col-span-2 mt-4 text-2xl items-center flex">
          <NavLink href="">{t("Nav_Home")}</NavLink>
          <NavLink href="/bundesliga/gameschedule">
            {t("Nav_Bundesliga")}
          </NavLink>
          <NavLink href="/pokal/gameschedule">{t("Nav_Pokal")}</NavLink>
          <NavLink href="/teams">{t("Nav_Teams")}</NavLink>
          <NavLink href="/players">{t("Nav_Players")}</NavLink>
          <NavLink href="/venues">{t("Nav_Venues")}</NavLink>
        </div>
        <div className="serviceNav ml-auto ">
          {/* <button
                  className=""
                  onClick={logout}
                >
                  <FaUser />
                </button> */}
          <div className="self-center hover:underline">
            <LanguageSwitch type="small" />
          </div>
          <NavLink href="/settings/user" className="self-center">
            <span title={t("Nav_Settings") as string}>
              <FiSettings />
            </span>
          </NavLink>
          <button onClick={logout} className="self-center">
            <span title="Logout">
              <FiLogOut />
            </span>
          </button>
        </div>
      </div>
      {!hasSubnav && (
        <div className="flex justify-between container mt-10">
          <h1>{t(getTitle(getCurrentlink(pathname)))}</h1>
          {categoryIsLast && (
            <AddButton
              category={getCurrentlink(pathname)}
              seasonId={seasonId}
            />
          )}
        </div>
      )}
      <Subnav section={getCurrentlink(pathname)} />
    </>
  );
}

interface SubnavLink {
  title: string;
  href: string;
}
interface SubnavProps {
  section: string;
}
export function Subnav({ section }: SubnavProps) {
  const { t } = useTranslation();
  const links: SubnavLink[] = [];
  let title: string | undefined = undefined;
  switch (section) {
    case "pokal":
      links.push({ title: t("Title_Gameschedule"), href: "/gameschedule" });
      links.push({ title: t("Nav_Teams"), href: "/teams" });
      title = t("Nav_Pokal") as string;
      break;
    case "bundesliga":
      links.push({ title: t("Title_Gameschedule"), href: "/gameschedule" });
      links.push({ title: t("Nav_Table"), href: "/table" });
      links.push({ title: t("Title_Rankings"), href: "/playerranking" });
      links.push({ title: t("Nav_Teams"), href: "/teams" });
      links.push({ title: "Berechnung", href: "/calculateBundesliga" });
      title = t("Nav_Bundesliga") as string;
      break;
    case "settings":
      links.push({ title: t("Nav_User"), href: "/user" });
      links.push({ title: t("Nav_Season"), href: "/season" });
      links.push({ title: t("Nav_Outfitter"), href: "/outfitter" });
      links.push({ title: t("Nav_Current_Season"), href: "/currentSeason" });
      title = t("Nav_Settings") as string;
    default:
      break;
  }

  return title ? (
    <div className="bg-neutral-200 w-full sticky top-0 z-10">
      <div className="beSubNav flex gap-2 align-bottom text-lg p-4 container">
        <h2>{title}</h2>
        {links.map((link) => (
          <NavLink key={link.title} href={"/" + section + link.href} isSubnav>
            {link.title}
          </NavLink>
        ))}
      </div>
    </div>
  ) : null;
}

interface NavLinkProps extends WithClassName {
  href: string;
  prefix?: string;
  isSubnav?: boolean;
}
function NavLink({
  href,
  children,
  className,
  isSubnav = false,
}: NavLinkProps) {
  const { pathname } = useRouter();
  const { customUrl } = useUrl();

  const currentCategory = "/" + pathname.split("/")[3];
  const currentEndpoint = "/" + pathname.split("/")[4];
  const linkCategory = "/" + href.split("/")[1];
  const linkEndpoint = "/" + href.split("/").slice(-1);
  const style = isSubnav
    ? currentEndpoint === linkEndpoint
      ? "secondaryBtn active"
      : "secondaryBtn"
    : currentCategory === linkCategory
    ? "secondaryBtn active h-fit flex flex-col"
    : "secondaryBtn h-fit flex flex-col";
  return (
    <Link
      href={customUrl(href)}
      className={"inline-block " + style + (className ?? "")}
    >
      {children}
    </Link>
  );
}
